import {
  Box,
  Button,
  Heading,
  PopoverBody,
  PopoverContent,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue,
  useOutsideClick,
  Spacer,
  Switch,
  HStack,
  Input,
  Spinner,
} from '@chakra-ui/react';
import { useFlag } from '@unleash/proxy-client-react';
import { isNil } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useQuery } from 'urql';
import {
  faCheck,
  faChevronDown,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import SparkelIcon from '../../common/icon/SparkelIcon';
import {
  displayNameFromAlgorithm,
  displayNameFromProperty,
} from '../../../domain/friendly-properties';
import {
  GeometricAlgorithmType,
  isGeometricAlgorithmType,
} from '../../../domain/geometry/algorithm-types';
import {
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import {
  PropertyContext,
  resolveDynamicQuantity,
} from '../../../domain/property-operations';
import { ResolvedQuantity } from '../../../domain/property-types';
import {
  CalculationType,
  CalculationTypeInput,
  DatabaseLookupType,
  DatabaseLookupTypeInput,
  ElementIdentifierDbidType,
  GetCompanyDatabaseDocument,
  GetCompanyDatabaseLookupProfilesDocument,
  ListCompanyDatabasesDocument,
  PropertySetType,
  QuantityOperation,
  UnitSettingsType,
} from '../../../gql/graphql';
import { useGeometricAlgorithms } from '../../../hooks/geometric-algorithms';
import { PropertyCascader } from '../../common/PropertyCascader';
import {
  ColumnNameAndId,
  parseSearchTerm,
  stringifySearchTerm,
} from '../../../utils/formula-utils';

type DynamicValuePopoverProps = {
  allowSumOnly?: boolean;
  propertyContext: PropertyContext;
  propertySets: PropertySetType[];
  dbIds: ElementIdentifierDbidType[];
  defaultCalculation: CalculationType | undefined | null;
  defaultDatabaseLookup?: DatabaseLookupType | null;
  orderColumns?: ColumnNameAndId[];
  onClose: () => void;
  onLink: (
    calculation: CalculationTypeInput | null,
    quantity: ResolvedQuantity | null,
    unitFormatOverride?: UnitSettingsType,
    databaseLookup?: DatabaseLookupTypeInput
  ) => void;
  onUnlink: () => void;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
};

type LinkedCountFormData = {
  selectedSource: CalculationSource.LINKED_COUNT;
};

// Removed separate Quantities/Location sources; use Property cascader instead

type SparkelCalculationsFormData = {
  selectedSource: CalculationSource.PROPERTIES_CALCULATIONS;
  selectedAlgorithm: GeometricAlgorithmType | null;
  sumValues: boolean;
};

type BIMPropertyFormData = {
  selectedSource: CalculationSource.BIM_PROPERTY;
  selectedAttributeName: string | null;
  selectedCategory: string | null;
  sumValues: boolean;
};

type DatabaseLookupFormData = {
  selectedSource: CalculationSource.DATABASE_LOOKUP;
  selectedDatabaseId: string | null;
  selectedValueColumnId: string | null;
  selectedSearchColumnId: string | null;
  selectedLookupProfileId: string | null;
  searchTerm: string;
};

type DynamicValuePopoverFormData =
  | LinkedCountFormData
  | BIMPropertyFormData
  | SparkelCalculationsFormData
  | DatabaseLookupFormData;

enum CalculationSource {
  BIM_PROPERTY = 'Property',
  PROPERTIES_CALCULATIONS = 'Calculations',
  LINKED_COUNT = 'Linked count',
  DATABASE_LOOKUP = 'Database Lookup',
}

const getSourceOptions = (enableCompanyDatabases: boolean) => {
  const sources = [
    CalculationSource.BIM_PROPERTY,
    CalculationSource.PROPERTIES_CALCULATIONS,
    CalculationSource.LINKED_COUNT,
  ];
  if (enableCompanyDatabases) {
    sources.push(CalculationSource.DATABASE_LOOKUP);
  }
  return sources.map((source) => ({
    label: source,
    value: source,
  }));
};

// Map existing calculation to form defaults
// eslint-disable-next-line complexity
const mapCalculationTypeToFormData = (
  defaultCalculation: CalculationType | null | undefined,
  defaultDatabaseLookup: DatabaseLookupType | null | undefined,
  allowSumOnly: boolean | undefined,
  orderColumns: ColumnNameAndId[] = []
): DynamicValuePopoverFormData => {
  // Handle database lookup
  if (defaultDatabaseLookup) {
    // Stringify the search term for display (convert var_xxx to $column_name)
    const displaySearchTerm = defaultDatabaseLookup.searchTerm
      ? stringifySearchTerm(defaultDatabaseLookup.searchTerm, orderColumns)
      : '';

    return {
      selectedSource: CalculationSource.DATABASE_LOOKUP,
      selectedDatabaseId: defaultDatabaseLookup.databaseId ?? null,
      selectedValueColumnId: defaultDatabaseLookup.valueColumnId ?? null,
      selectedSearchColumnId: defaultDatabaseLookup.searchColumnId ?? null,
      selectedLookupProfileId: defaultDatabaseLookup.lookupProfileId ?? null,
      searchTerm: displaySearchTerm,
    };
  }

  const defaultCategory = defaultCalculation?.attribute?.category;
  const defaultAttributeName = defaultCalculation?.attribute?.name;
  if (defaultCalculation?.operation === QuantityOperation.Count) {
    return { selectedSource: CalculationSource.LINKED_COUNT };
  }
  const sumValues =
    allowSumOnly || defaultCalculation?.operation === QuantityOperation.Sum
      ? true
      : false;

  if (defaultCategory === SPARKEL_GEOMETRIC_PROPERTY_SET) {
    if (isGeometricAlgorithmType(defaultAttributeName ?? '')) {
      return {
        selectedSource: CalculationSource.PROPERTIES_CALCULATIONS,
        selectedAlgorithm: (defaultAttributeName ??
          null) as GeometricAlgorithmType | null,
        sumValues,
      };
    } else {
      return {
        selectedSource: CalculationSource.BIM_PROPERTY,
        selectedCategory: defaultCategory ?? null,
        selectedAttributeName: defaultAttributeName ?? null,
        sumValues,
      };
    }
  } else if (defaultCategory === SPARKEL_LOCATION_PROPERTY_SET) {
    return {
      selectedSource: CalculationSource.BIM_PROPERTY,
      selectedCategory: defaultCategory ?? null,
      selectedAttributeName: defaultAttributeName ?? null,
      sumValues,
    };
  } else {
    return {
      selectedSource: CalculationSource.BIM_PROPERTY,
      selectedCategory: defaultCategory ?? null,
      selectedAttributeName: defaultAttributeName ?? null,
      sumValues,
    };
  }
};

const mapFormDataToCalculationType = (
  formValues: DynamicValuePopoverFormData,
  forceSum?: boolean
): CalculationTypeInput | null => {
  // Database lookup doesn't use calculation
  if (formValues.selectedSource === CalculationSource.DATABASE_LOOKUP) {
    return null;
  }

  const operation =
    formValues.selectedSource === CalculationSource.LINKED_COUNT
      ? QuantityOperation.Count
      : forceSum
      ? QuantityOperation.Sum
      : formValues.sumValues
      ? QuantityOperation.Sum
      : QuantityOperation.Extract;

  if (formValues.selectedSource === CalculationSource.LINKED_COUNT) {
    return { operation };
  } else if (
    formValues.selectedSource === CalculationSource.PROPERTIES_CALCULATIONS
  ) {
    return {
      operation,
      attribute: {
        category: SPARKEL_GEOMETRIC_PROPERTY_SET,
        name: formValues.selectedAlgorithm as string,
      },
    };
  } else {
    invariant(
      (formValues as BIMPropertyFormData).selectedCategory,
      'Category is not set'
    );
    invariant(
      (formValues as BIMPropertyFormData).selectedAttributeName,
      'Attribute is not set'
    );
    return {
      operation,
      attribute: {
        category: (formValues as BIMPropertyFormData)
          .selectedCategory as string,
        name: (formValues as BIMPropertyFormData)
          .selectedAttributeName as string,
      },
    };
  }
};

const mapFormDataToDatabaseLookup = (
  formValues: DynamicValuePopoverFormData,
  orderColumns: ColumnNameAndId[] = [],
  enableLookupProfiles = false
): DatabaseLookupTypeInput | undefined => {
  if (formValues.selectedSource !== CalculationSource.DATABASE_LOOKUP) {
    return undefined;
  }
  const data = formValues as DatabaseLookupFormData;
  if (
    !data.selectedDatabaseId ||
    !data.selectedValueColumnId ||
    !data.selectedSearchColumnId
  ) {
    return undefined;
  }

  // Parse the search term (convert $column_name to var_xxx for storage)
  const parsedSearchTerm = parseSearchTerm(data.searchTerm, orderColumns);

  return {
    databaseId: data.selectedDatabaseId,
    valueColumnId: data.selectedValueColumnId,
    searchColumnId: data.selectedSearchColumnId,
    searchTerm: parsedSearchTerm,
    // Only include lookupProfileId if the feature flag is enabled
    lookupProfileId: enableLookupProfiles
      ? data.selectedLookupProfileId ?? undefined
      : undefined,
  };
};

// eslint-disable-next-line complexity
export default function DynamicValuePopover({
  propertyContext,
  allowSumOnly,
  propertySets,
  dbIds,
  defaultCalculation,
  defaultDatabaseLookup,
  orderColumns = [],
  onClose,
  onLink,
  onUnlink,
  projectSettings,
}: DynamicValuePopoverProps): React.ReactElement {
  const { t } = useTranslation(['table', 'common']);
  const enableLlmLookupProfiles = useFlag('enable-lookup-profiles');
  const enableCompanyDatabases = useFlag('enable-company-databases');
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isUsingMenu, setIsUsingMenu] = useState(false);
  useOutsideClick({
    ref: popoverRef,
    handler: () => {
      if (!isUsingMenu) {
        onClose();
      }
    },
  });

  const algorithms = useGeometricAlgorithms();
  const calculationOptions = useMemo(
    () =>
      algorithms
        .filter((algorithm) => !algorithm.toLowerCase().includes('location'))
        .map((algorithm) => ({
          label: displayNameFromAlgorithm(algorithm, t),
          value: algorithm,
        })),
    [algorithms, t]
  );

  const sourceOptions = useMemo(
    () => getSourceOptions(enableCompanyDatabases),
    [enableCompanyDatabases]
  );

  const defaultValues = mapCalculationTypeToFormData(
    defaultCalculation,
    defaultDatabaseLookup,
    allowSumOnly,
    orderColumns
  );

  const { watch, handleSubmit, setValue, control } =
    useForm<DynamicValuePopoverFormData>({
      defaultValues,
    });

  const selectedSource = watch('selectedSource');
  const selectedCategory = watch('selectedCategory');
  const selectedAttributeName = watch('selectedAttributeName');

  // Database lookup state
  const selectedDatabaseId = watch('selectedDatabaseId');

  // Fetch company databases
  const [{ data: databasesData, fetching: fetchingDatabases }] = useQuery({
    query: ListCompanyDatabasesDocument,
  });

  // Fetch selected database with columns
  const [{ data: databaseData, fetching: fetchingDatabase }] = useQuery({
    query: GetCompanyDatabaseDocument,
    variables: { id: selectedDatabaseId ?? '' },
    pause: !selectedDatabaseId,
  });

  // Fetch lookup profiles for the selected database (only when feature flag is enabled)
  const [{ data: lookupProfilesData, fetching: fetchingLookupProfiles }] =
    useQuery({
      query: GetCompanyDatabaseLookupProfilesDocument,
      variables: { databaseId: selectedDatabaseId ?? '' },
      pause: !selectedDatabaseId || !enableLlmLookupProfiles,
    });

  const databases = databasesData?.companyDatabases ?? [];
  const selectedDatabase = databaseData?.companyDatabasis;
  const columns = selectedDatabase?.companyDatabaseColumnsByDatabaseId ?? [];
  const lookupProfiles =
    lookupProfilesData?.companyDatabaseLookupProfiles ?? [];

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build friendly cascader data directly from propertySets

  const propertyByCategoryWithFriendlyNames = useMemo(
    () =>
      propertySets.map((set) => {
        if (set.category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
          return {
            label: t('common:quantities'),
            children: set.properties.map((property) => {
              return { label: displayNameFromProperty(property, t) };
            }),
          };
        } else if (set.category === SPARKEL_LOCATION_PROPERTY_SET) {
          return {
            label: t('common:location'),
            children: set.properties.map((property) => {
              return { label: displayNameFromProperty(property, t) };
            }),
          };
        } else {
          return {
            label: set.category,
            children: set.properties.map((property) => {
              return { label: property };
            }),
          };
        }
      }),
    [propertySets, t]
  );

  // Legacy options arrays removed in favor of PropertyCascader for Property selection

  const cascaderPlaceholder = useMemo(() => {
    if (selectedCategory && selectedAttributeName) {
      let categoryLabel = selectedCategory;
      if (selectedCategory === SPARKEL_GEOMETRIC_PROPERTY_SET) {
        categoryLabel = t('common:quantities');
      } else if (selectedCategory === SPARKEL_LOCATION_PROPERTY_SET) {
        categoryLabel = t('common:location');
      }
      const propertyLabel =
        selectedCategory === SPARKEL_GEOMETRIC_PROPERTY_SET ||
        selectedCategory === SPARKEL_LOCATION_PROPERTY_SET
          ? displayNameFromProperty(selectedAttributeName, t)
          : selectedAttributeName;
      return `${categoryLabel} / ${propertyLabel}`;
    }
    return t('table:breakdown.assign.placeholder');
  }, [selectedCategory, selectedAttributeName, t]);

  const onSubmitInternal = handleSubmit(async (values) => {
    try {
      setIsSubmitting(true);

      // Handle database lookup
      if (values.selectedSource === CalculationSource.DATABASE_LOOKUP) {
        const databaseLookup = mapFormDataToDatabaseLookup(
          values,
          orderColumns,
          enableLlmLookupProfiles
        );
        if (databaseLookup) {
          onLink(null, null, undefined, databaseLookup);
          onClose();
        }
        return;
      }

      const calculation = mapFormDataToCalculationType(values, allowSumOnly);

      let quantity: ResolvedQuantity | null = null;
      if (
        !isNil(calculation) &&
        !isNil(calculation?.attribute?.name) &&
        dbIds.some((dbId) => dbId.dbIds.length > 0)
      ) {
        try {
          quantity = await resolveDynamicQuantity(
            propertyContext,
            dbIds,
            calculation,
            projectSettings
          );
        } catch (error) {
          // Swallow errors; caller can proceed without preview quantity
          // eslint-disable-next-line no-console
          console.error('Error resolving dynamic quantity', error);
        }
      }
      onLink(calculation, quantity);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  });

  const selectedBackgroundColor = useColorModeValue('blue.50', 'blue.800');
  const selectedTextColor = useColorModeValue('blue.700', 'blue.200');
  const textColor = useColorModeValue('gray.800', 'gray.50');

  return (
    <PopoverContent
      shadow={'lg'}
      width={
        selectedSource === CalculationSource.DATABASE_LOOKUP &&
        selectedDatabaseId
          ? 380
          : 240
      }
    >
      <PopoverBody ref={popoverRef}>
        <Heading as={'h3'} size={'xs'} marginY={1}>
          {t('table:set-column-dynamic-value')}
        </Heading>
        <VStack spacing={2} align="stretch">
          <Box>
            <Text
              alignSelf={'flex-start'}
              fontSize={'xs'}
              marginLeft={1}
              marginTop={2}
              color="gray.500"
            >
              Source
            </Text>
            <Controller
              control={control}
              name="selectedSource"
              render={({ field: { onChange, value } }) => (
                <Menu
                  onOpen={() => setIsUsingMenu(true)}
                  onClose={() => setIsUsingMenu(false)}
                >
                  <MenuButton
                    as={Button}
                    size="xs"
                    colorScheme="gray"
                    borderRadius="md"
                    marginTop={1}
                    width="100%"
                    textAlign="left"
                    fontWeight="normal"
                    color={value ? 'inherit' : 'gray.500'}
                    rightIcon={
                      <SparkelIcon
                        icon={faChevronDown}
                        size="xs"
                        color="inherit"
                      />
                    }
                  >
                    {sourceOptions.find((o) => o.value === value)?.label ||
                      t('table:breakdown.assign.placeholder')}
                  </MenuButton>
                  <MenuList padding={1} borderRadius="md">
                    {sourceOptions.map((opt) => (
                      <MenuItem
                        borderRadius="md"
                        key={opt.value}
                        backgroundColor={
                          value === opt.value
                            ? selectedBackgroundColor
                            : undefined
                        }
                        color={
                          value === opt.value ? selectedTextColor : undefined
                        }
                        fontWeight={value === opt.value ? 'bold' : 'normal'}
                        onClick={() => {
                          onChange(opt.value as CalculationSource);
                          setValue(
                            'selectedAttributeName',
                            null as unknown as string
                          );
                          setValue(
                            'selectedCategory',
                            null as unknown as string
                          );
                          // cleared via cascader selection for Property
                          setValue(
                            'selectedAlgorithm',
                            null as unknown as GeometricAlgorithmType
                          );
                          // Clear database lookup fields
                          setValue(
                            'selectedDatabaseId',
                            null as unknown as string
                          );
                          setValue(
                            'selectedValueColumnId',
                            null as unknown as string
                          );
                          setValue(
                            'selectedSearchColumnId',
                            null as unknown as string
                          );
                          setValue(
                            'selectedLookupProfileId',
                            null as unknown as string
                          );
                          setValue('searchTerm', '');
                        }}
                      >
                        {opt.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            />
          </Box>

          {selectedSource === CalculationSource.BIM_PROPERTY ? (
            <Box>
              <Text
                alignSelf={'flex-start'}
                fontSize={'xs'}
                marginLeft={1}
                marginTop={2}
                color="gray.500"
              >
                Property
              </Text>
              <Box marginTop={1}>
                <PropertyCascader
                  aria-label="bim property cascader"
                  data={propertyByCategoryWithFriendlyNames}
                  size="xs"
                  variant={'filled'}
                  closeOnSelect={true}
                  onCascaderOpen={() => setIsUsingMenu(true)}
                  onCascaderClose={() => setIsUsingMenu(false)}
                  defaultSelection={(function () {
                    const categoryIndex = propertySets.findIndex(
                      (s) => s.category === selectedCategory
                    );
                    if (categoryIndex === -1) return undefined;
                    const propIndex = propertySets[
                      categoryIndex
                    ].properties.findIndex((p) => p === selectedAttributeName);
                    if (propIndex === -1) return undefined;
                    return [categoryIndex, propIndex];
                  })()}
                  placeholder={cascaderPlaceholder}
                  _placeholder={{
                    color:
                      selectedCategory && selectedAttributeName
                        ? textColor
                        : undefined,
                  }}
                  onPropertySelect={(selectedIndices) => {
                    const [catIdx, propIdx] = selectedIndices;
                    if (
                      typeof catIdx === 'number' &&
                      typeof propIdx === 'number' &&
                      propertySets[catIdx] &&
                      propertySets[catIdx].properties[propIdx]
                    ) {
                      setValue(
                        'selectedCategory',
                        propertySets[catIdx].category
                      );
                      setValue(
                        'selectedAttributeName',
                        propertySets[catIdx].properties[propIdx]
                      );
                    }
                  }}
                />
              </Box>
            </Box>
          ) : null}

          {/* Quantities and Location are selected via the Property cascader */}

          {/* Removed old BIM property dropdowns in favor of PropertyCascader */}

          {selectedSource === CalculationSource.PROPERTIES_CALCULATIONS ? (
            <Box>
              <Text
                alignSelf={'flex-start'}
                fontSize={'xs'}
                marginLeft={1}
                marginTop={2}
                color="gray.500"
              >
                {t('common:calculations')}
              </Text>
              <Controller
                control={control}
                name="selectedAlgorithm"
                render={({ field: { onChange, value } }) => (
                  <Menu
                    onOpen={() => setIsUsingMenu(true)}
                    onClose={() => setIsUsingMenu(false)}
                    placement="top"
                  >
                    <MenuButton
                      as={Button}
                      size="xs"
                      colorScheme="gray"
                      borderRadius="md"
                      marginTop={1}
                      width="100%"
                      textAlign="left"
                      fontWeight="normal"
                      color={value ? 'inherit' : 'gray.500'}
                      rightIcon={
                        <SparkelIcon
                          icon={faChevronDown}
                          size="xs"
                          color="inherit"
                        />
                      }
                    >
                      <Text noOfLines={1}>
                        {(value as GeometricAlgorithmType | null)
                          ? displayNameFromAlgorithm(
                              value as GeometricAlgorithmType,
                              t
                            )
                          : t('table:breakdown.assign.placeholder')}
                      </Text>
                    </MenuButton>
                    <MenuList padding={1} maxHeight={300} overflowY="auto">
                      {calculationOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          borderRadius="md"
                          fontWeight={value === opt.value ? 'bold' : 'normal'}
                          backgroundColor={
                            value === opt.value
                              ? selectedBackgroundColor
                              : undefined
                          }
                          color={
                            value === opt.value ? selectedTextColor : undefined
                          }
                          onClick={() => {
                            onChange(opt.value);
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
              />
            </Box>
          ) : null}

          {/* Database Lookup */}
          {selectedSource === CalculationSource.DATABASE_LOOKUP ? (
            <>
              {/* Database Selection */}
              <Box>
                <Text
                  alignSelf={'flex-start'}
                  fontSize={'xs'}
                  marginLeft={1}
                  marginTop={2}
                  color="gray.500"
                >
                  {t('database-lookup.database')}
                </Text>
                <Controller
                  control={control}
                  name="selectedDatabaseId"
                  render={({ field: { onChange, value } }) => (
                    <Menu
                      onOpen={() => setIsUsingMenu(true)}
                      onClose={() => setIsUsingMenu(false)}
                    >
                      <MenuButton
                        as={Button}
                        size="xs"
                        colorScheme="gray"
                        borderRadius="md"
                        marginTop={1}
                        width="100%"
                        textAlign="left"
                        fontWeight="normal"
                        color={value ? 'inherit' : 'gray.500'}
                        rightIcon={
                          fetchingDatabases ? (
                            <Spinner size="xs" />
                          ) : (
                            <SparkelIcon
                              icon={faChevronDown}
                              size="xs"
                              color="inherit"
                            />
                          )
                        }
                      >
                        <Text noOfLines={1}>
                          {databases.find((db) => db.id === value)?.name ||
                            t('database-lookup.select-database')}
                        </Text>
                      </MenuButton>
                      <MenuList padding={1} maxHeight={200} overflowY="auto">
                        {databases.map((db) => (
                          <MenuItem
                            key={db.id}
                            borderRadius="md"
                            fontWeight={value === db.id ? 'bold' : 'normal'}
                            backgroundColor={
                              value === db.id
                                ? selectedBackgroundColor
                                : undefined
                            }
                            color={
                              value === db.id ? selectedTextColor : undefined
                            }
                            onClick={() => {
                              onChange(db.id);
                              // Reset column selections and profile when database changes
                              setValue(
                                'selectedValueColumnId',
                                null as unknown as string
                              );
                              setValue(
                                'selectedSearchColumnId',
                                null as unknown as string
                              );
                              setValue(
                                'selectedLookupProfileId',
                                null as unknown as string
                              );
                            }}
                          >
                            {db.name}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                />
              </Box>

              {/* Lookup Profile Selection (optional) - only show if profiles exist */}
              {enableLlmLookupProfiles &&
                selectedDatabaseId &&
                lookupProfiles.length > 0 && (
                  <Box>
                    <Text
                      alignSelf={'flex-start'}
                      fontSize={'xs'}
                      marginLeft={1}
                      marginTop={2}
                      color="gray.500"
                    >
                      {t('database-lookup.lookup-profile')}
                    </Text>
                    <Controller
                      control={control}
                      name="selectedLookupProfileId"
                      render={({ field: { onChange, value } }) => (
                        <Menu
                          onOpen={() => setIsUsingMenu(true)}
                          onClose={() => setIsUsingMenu(false)}
                        >
                          <MenuButton
                            as={Button}
                            size="xs"
                            colorScheme="gray"
                            borderRadius="md"
                            marginTop={1}
                            width="100%"
                            textAlign="left"
                            fontWeight="normal"
                            color={value ? 'inherit' : 'gray.500'}
                            rightIcon={
                              fetchingLookupProfiles ? (
                                <Spinner size="xs" />
                              ) : (
                                <SparkelIcon
                                  icon={faChevronDown}
                                  size="xs"
                                  color="inherit"
                                />
                              )
                            }
                          >
                            <Text noOfLines={1}>
                              {lookupProfiles.find(
                                (profile) => profile.id === value
                              )?.name || t('database-lookup.none')}
                            </Text>
                          </MenuButton>
                          <MenuList
                            padding={1}
                            maxHeight={200}
                            overflowY="auto"
                          >
                            <MenuItem
                              borderRadius="md"
                              fontWeight={!value ? 'bold' : 'normal'}
                              backgroundColor={
                                !value ? selectedBackgroundColor : undefined
                              }
                              color={!value ? selectedTextColor : undefined}
                              onClick={() => onChange(null)}
                            >
                              {t('database-lookup.none')}
                            </MenuItem>
                            {lookupProfiles.map((profile) => (
                              <MenuItem
                                key={profile.id}
                                borderRadius="md"
                                fontWeight={
                                  value === profile.id ? 'bold' : 'normal'
                                }
                                backgroundColor={
                                  value === profile.id
                                    ? selectedBackgroundColor
                                    : undefined
                                }
                                color={
                                  value === profile.id
                                    ? selectedTextColor
                                    : undefined
                                }
                                onClick={() => onChange(profile.id)}
                              >
                                {profile.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      )}
                    />
                  </Box>
                )}

              {/* Lookup Column and Search Value - side by side */}
              {selectedDatabaseId && (
                <HStack spacing={2} alignItems="flex-start" marginTop={2}>
                  {/* Search Column Selection */}
                  <Box flex={1}>
                    <Text
                      alignSelf={'flex-start'}
                      fontSize={'xs'}
                      marginLeft={1}
                      color="gray.500"
                    >
                      {t('database-lookup.lookup-column')}
                    </Text>
                    <Controller
                      control={control}
                      name="selectedSearchColumnId"
                      render={({ field: { onChange, value } }) => (
                        <Menu
                          onOpen={() => setIsUsingMenu(true)}
                          onClose={() => setIsUsingMenu(false)}
                        >
                          <MenuButton
                            as={Button}
                            size="xs"
                            colorScheme="gray"
                            borderRadius="md"
                            marginTop={1}
                            width="100%"
                            textAlign="left"
                            fontWeight="normal"
                            color={value ? 'inherit' : 'gray.500'}
                            rightIcon={
                              <SparkelIcon
                                icon={faChevronDown}
                                size="xs"
                                color="inherit"
                              />
                            }
                          >
                            <Text noOfLines={1}>
                              {columns.find((col) => col.id === value)?.name ||
                                t('database-lookup.select-column')}
                            </Text>
                          </MenuButton>
                          <MenuList
                            padding={1}
                            maxHeight={200}
                            overflowY="auto"
                          >
                            {columns.map((col) => (
                              <MenuItem
                                key={col.id}
                                borderRadius="md"
                                fontWeight={
                                  value === col.id ? 'bold' : 'normal'
                                }
                                backgroundColor={
                                  value === col.id
                                    ? selectedBackgroundColor
                                    : undefined
                                }
                                color={
                                  value === col.id
                                    ? selectedTextColor
                                    : undefined
                                }
                                onClick={() => onChange(col.id)}
                              >
                                {col.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      )}
                    />
                  </Box>

                  {/* Search Term Input */}
                  <Box flex={1}>
                    <Text
                      alignSelf={'flex-start'}
                      fontSize={'xs'}
                      marginLeft={1}
                      color="gray.500"
                    >
                      {t('database-lookup.search-value')}
                    </Text>
                    <Controller
                      control={control}
                      name="searchTerm"
                      render={({ field: { onChange, value } }) => (
                        <Input
                          size="xs"
                          marginTop={1}
                          variant="filled"
                          borderRadius="md"
                          placeholder="$name"
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value)}
                          onFocus={() => setIsUsingMenu(true)}
                          onBlur={() => setIsUsingMenu(false)}
                        />
                      )}
                    />
                  </Box>
                </HStack>
              )}

              {/* Search value hint */}
              {selectedDatabaseId && (
                <Text
                  fontSize={'2xs'}
                  color="gray.400"
                  marginLeft={1}
                  marginTop={1}
                >
                  {t('database-lookup.search-value-hint')}
                </Text>
              )}

              {/* Value Column Selection (column to return) */}
              {selectedDatabaseId && (
                <Box>
                  <Text
                    alignSelf={'flex-start'}
                    fontSize={'xs'}
                    marginLeft={1}
                    marginTop={2}
                    color="gray.500"
                  >
                    {t('database-lookup.return-column')}
                  </Text>
                  <Controller
                    control={control}
                    name="selectedValueColumnId"
                    render={({ field: { onChange, value } }) => (
                      <Menu
                        onOpen={() => setIsUsingMenu(true)}
                        onClose={() => setIsUsingMenu(false)}
                      >
                        <MenuButton
                          as={Button}
                          size="xs"
                          colorScheme="gray"
                          borderRadius="md"
                          marginTop={1}
                          width="100%"
                          textAlign="left"
                          fontWeight="normal"
                          color={value ? 'inherit' : 'gray.500'}
                          rightIcon={
                            fetchingDatabase ? (
                              <Spinner size="xs" />
                            ) : (
                              <SparkelIcon
                                icon={faChevronDown}
                                size="xs"
                                color="inherit"
                              />
                            )
                          }
                        >
                          <Text noOfLines={1}>
                            {columns.find((col) => col.id === value)?.name ||
                              t('database-lookup.select-column')}
                          </Text>
                        </MenuButton>
                        <MenuList padding={1} maxHeight={200} overflowY="auto">
                          {columns.map((col) => (
                            <MenuItem
                              key={col.id}
                              borderRadius="md"
                              fontWeight={value === col.id ? 'bold' : 'normal'}
                              backgroundColor={
                                value === col.id
                                  ? selectedBackgroundColor
                                  : undefined
                              }
                              color={
                                value === col.id ? selectedTextColor : undefined
                              }
                              onClick={() => onChange(col.id)}
                            >
                              {col.name}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    )}
                  />
                </Box>
              )}
            </>
          ) : null}

          {/* Sum values checkbox */}
          {selectedSource !== CalculationSource.LINKED_COUNT &&
          selectedSource !== CalculationSource.DATABASE_LOOKUP &&
          !allowSumOnly ? (
            <HStack marginTop={3}>
              <Controller
                control={control}
                name="sumValues"
                render={({ field: { onChange, value } }) => (
                  <Switch
                    size="sm"
                    colorScheme="cyan"
                    isChecked={allowSumOnly ? false : value ?? false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
              <Text alignSelf={'flex-start'} fontSize={'xs'} color="gray.500">
                Sum values
              </Text>
            </HStack>
          ) : null}

          {/* Action buttons */}
          <Box display="flex" marginTop={2}>
            <Button
              colorScheme="red"
              variant="ghost"
              size="xs"
              onClick={() => {
                onUnlink();
                onClose();
              }}
              isDisabled={isSubmitting || !defaultCalculation}
              leftIcon={<SparkelIcon icon={faTimes} color="inherit" />}
            >
              Unassign
            </Button>
            <Spacer />
            <Button
              colorScheme="brand"
              size="xs"
              onClick={onSubmitInternal}
              isLoading={isSubmitting}
              variant="ghost"
              leftIcon={<SparkelIcon icon={faCheck} color="inherit" />}
            >
              Apply
            </Button>
          </Box>
        </VStack>
      </PopoverBody>
    </PopoverContent>
  );
}
